/**
 * @file This file contains the WeatherManager class for creating ambient, non-gameplay weather effects.
 * @module WeatherManager
 */

import { weatherConfig } from '../config/weatherConfig.js';

export class WeatherManager {
    /**
     * @param {object} dependencies - The dependencies for the WeatherManager.
     * @param {HTMLElement} dependencies.overlayElement - The DOM element to render weather effects into.
     * @param {HTMLElement} dependencies.dayNightOverlay - The DOM element for the day/night cycle tint.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The global event emitter.
     * @param {HTMLElement} dependencies.dayNightToggleBtn - The button to toggle day/night.
     * @param {import('./PreferencesManager.js').PreferencesManager} dependencies.preferencesManager - The preferences manager.
     * @param {import('../Game.js').Game} dependencies.game - The main game instance.
     */
    constructor({ overlayElement, dayNightOverlay, eventEmitter, dayNightToggleBtn, preferencesManager, game }) {
        this.overlay = overlayElement;
        this.dayNightOverlay = dayNightOverlay;
        this.eventEmitter = eventEmitter;
        this.dayNightToggleBtn = dayNightToggleBtn;
        this.preferencesManager = preferencesManager; // New: PreferencesManager
        this.game = game;
        this.config = weatherConfig;
        this.particles = [];
        this.activeWeather = null;
        this.turnsRemaining = 0;
        this.turnCounter = 0;
        this.idleTimer = null;
        this.gameConfig = null; // Will be set when a game starts
        this.isDayNightCycleEnabled = false;

        this.timeOfDay = 0; // 0 = midnight, 0.5 = noon
        this.dayNightCycleDuration = 120000; // 2 minutes for a full cycle
        this.lastTimestamp = 0;

        this.eventEmitter.on('achievement:turnEnded', () => this.onTurnEnd());
        this.eventEmitter.on('game:start', () => {
            this.stopWeather(true); // Force stop and clear any existing weather from a previous session.
            this.resetIdleTimer();
        });
        this.eventEmitter.on('game:itemPlaced', () => this.resetIdleTimer());
        this.eventEmitter.on('game:powerupPlaced', () => this.resetIdleTimer());

        if (this.dayNightToggleBtn) {
            this.dayNightToggleBtn.addEventListener('click', () => this.toggleDayNight());
        }
    }

    /**
     * Sets the game configuration for the weather manager.
     * @param {object} gameConfig - The game configuration.
     */
    setGameConfig(gameConfig) {
        this.gameConfig = gameConfig;
        this.isDayNightCycleEnabled = this.gameConfig.mechanics?.isDayNightCycleEnabled ?? true;

        if (!this.isDayNightCycleEnabled) {
            this._applyDayNightVisuals(); // Ensure visuals are reset if disabled
        }
    }

    /**
     * Toggles the day/night cycle on and off.
     */
    toggleDayNight() {
        this.isDayNightCycleEnabled = !this.isDayNightCycleEnabled;
        // If we are disabling it, we need to apply the "day" visuals immediately.
        if (!this.isDayNightCycleEnabled) {
            this._applyDayNightVisuals();
        }
    }

    /**
     * Handles the end of a turn for weather effects.
     */
    onTurnEnd() {
        if (!this.preferencesManager.getPreference('enableWeatherEffects', true)) {
            return; // Do nothing if weather effects are disabled
        }

        if (this.activeWeather) {
            this.turnsRemaining--;
            if (this.turnsRemaining <= 0) {
                this.stopWeather();
            }
        } else {
            this.turnCounter++;
            for (const weatherKey in this.config) {
                const weather = this.config[weatherKey];
                if (this.turnCounter >= weather.frequency) {
                    if (Math.random() < 1 / weather.frequency) {
                        this.startWeather(weatherKey);
                        break; // Only start one weather effect
                    }
                }
            }
        }
    }

    /**
     * Resets the idle timer for weather effects.
     */
    resetIdleTimer() {
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
        }

        if (this.gameConfig?.mechanics?.weatherIdleTimeout && this.preferencesManager.getPreference('enableWeatherEffects', true)) {
            this.idleTimer = setTimeout(() => {
                this.triggerRandomWeather();
            }, this.gameConfig.mechanics.weatherIdleTimeout);
        }
    }

    /**
     * Triggers a random weather effect if none is currently active.
     */
    triggerRandomWeather() {
        // Do not trigger idle weather if the game loop isn't running (e.g., on a menu screen).
        if (!this.game.gameLoopId || this.game.gameController.isPaused) {
            this.resetIdleTimer(); // Restart the timer and wait for a better opportunity.
            return;
        }
        if (!this.preferencesManager.getPreference('enableWeatherEffects', true)) return;

        // If a weather effect is already officially active, let it finish.
        // The idle timer will be reset by its own completion logic.
        if (this.activeWeather) return;

        // A weather effect is not "active", but particles might be fading out from a a *previous* one.
        // To prevent visual compounding, we forcefully kill any lingering old particles before starting a new one.
        this.particles = [];
        this.overlay.innerHTML = '';

        const weatherKeys = Object.keys(this.config);
        if (weatherKeys.length === 0) return;

        const randomWeatherKey = weatherKeys[Math.floor(Math.random() * weatherKeys.length)];
        this.startWeather(randomWeatherKey);

        // When triggered by the idle timer, the new weather should also stop after a certain time.
        // We'll use the weather's configured turn duration to calculate a time duration.
        // We'll assume an average turn takes about 8 seconds for this calculation.
        const weather = this.config[randomWeatherKey];
        const durationInMs = (weather.duration || 5) * 8000; // Default to 5 turns if not specified

        setTimeout(() => {
            // Stop gracefully. This sets activeWeather to null, allowing existing particles to
            // move off-screen and be removed naturally by the update loop, creating a "fade out" effect.
            this.stopWeather();
            this.resetIdleTimer(); // Restart the idle countdown after the weather finishes.
        }, durationInMs);
    }

    /**
     * Starts a weather effect.
     * @param {string} weatherKey - The key of the weather to start.
     */
    startWeather(weatherKey) {
        if (!this.preferencesManager.getPreference('enableWeatherEffects', true)) {
            return; // Do nothing if weather effects are disabled
        }
        if (this.activeWeather) return;

        const weather = this.config[weatherKey];
        this.activeWeather = weatherKey;
        this.turnsRemaining = weather.duration;
        this.turnCounter = 0; // Reset counter

        for (let i = 0; i < weather.particle.count; i++) {
            this.createParticle(weather.particle, true);
        }
    }

    /**
     * A cheat function to immediately start a weather effect, clearing any existing one.
     * @param {string} weatherKey - The key of the weather to start.
     */
    forceStartWeather(weatherKey) {
        if (!this.preferencesManager.getPreference('enableWeatherEffects', true)) {
            return; // Do nothing if weather effects are disabled
        }
        this.stopWeather(true); // Force stop and clear existing particles
        this.startWeather(weatherKey);
    }

    /**
     * Stops the current weather effect.
     * @param {boolean} [force=false] - True to forcibly remove all particles immediately.
     */
    stopWeather(force = false) {
        this.activeWeather = null;
        this.turnsRemaining = 0;
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
        if (force) {
            this.particles = [];
            this.overlay.innerHTML = ''; // Forcibly remove all particle elements from the DOM.
        }
    }

    /**
     * Creates a weather particle.
     * @param {object} config - The configuration for the particle.
     * @param {boolean} [isInitial=false] - True if the particle is created on initial weather start.
     */
    createParticle(config, isInitial = false) {
        const particle = document.createElement('div');
        
        let displayContent = config.content;
        if (Array.isArray(displayContent)) {
            displayContent = displayContent[Math.floor(Math.random() * displayContent.length)];
        }
        particle.textContent = displayContent;

        particle.style.position = 'absolute';
        particle.style.fontSize = config.size || '1.2em';
        particle.style.userSelect = 'none';
        particle.classList.add('particle');

        if (config.className) {
            particle.classList.add(config.className);
        }

        // --- 3D Depth Effect ---
        const depth = 0.5 + Math.random(); // Range: 0.5 (far) to 1.5 (near)
        particle.style.zIndex = Math.round(depth * 10);

        let x, y;
        if (config.movement === 'traverse') {
            // Start off-screen to the left, at a random vertical position in the top 80% of the screen
            x = -100;
            y = Math.random() * (this.overlay.offsetHeight || window.innerHeight) * 0.8;
        } else {
            // Default starting position for falling/floating particles
            x = Math.random() * (this.overlay.offsetWidth || window.innerWidth);
            y = isInitial ? Math.random() * (this.overlay.offsetHeight || window.innerHeight) : -20;
        }

        this.overlay.appendChild(particle);
        this.particles.push({
            element: particle,
            x,
            y,
            speed: (config.baseSpeed || 2) + Math.random() * 2,
            wind: (config.wind || 0) * (Math.random() - 0.5) * 2,
            movement: config.movement || 'fall',
            angle: Math.random() * Math.PI * 2,
            depth: depth
        });

        // Set initial position using transform for smooth animation.
        particle.style.transform = `translate3d(${x}px, ${y}px, 100px) scale(${depth}) rotateX(0deg)`;
    }

    /**
     * Updates the weather effects and day/night cycle.
     * @param {number} deltaTime - The time elapsed since the last update.
     */
    update(deltaTime) {
        // --- Day/Night Cycle Logic ---
        if (this.isDayNightCycleEnabled) {
            this.timeOfDay = (this.timeOfDay + deltaTime / this.dayNightCycleDuration) % 1;
            this._applyDayNightVisuals();
        }

        // --- Particle and Effect Logic ---
        // Only update if weather effects are enabled OR if there are still particles to clear
        if (!this.preferencesManager.getPreference('enableWeatherEffects', true) && this.particles.length === 0) {
            return;
        }

        // If weather effects are disabled but there are still particles, stop weather and clear them
        if (!this.preferencesManager.getPreference('enableWeatherEffects', true) && this.particles.length > 0) {
            this.stopWeather(true);
            return;
        }

        if (!this.activeWeather && this.particles.length === 0) return;

        const weather = this.config[this.activeWeather];
        const overlayHeight = this.overlay.offsetHeight || window.innerHeight;
        const overlayWidth = this.overlay.offsetWidth || window.innerWidth;

        if (weather?.lightning && Math.random() < weather.lightning.frequency) {
            this.overlay.style.backgroundColor = 'rgba(200, 200, 255, 0.2)';
            setTimeout(() => {
                this.overlay.style.backgroundColor = 'transparent';
            }, 120);
        }

        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];

            switch (p.movement) {
            case 'float':
                p.angle += 0.02;
                p.y -= p.speed * p.depth; // Slow upward drift, faster when closer
                p.x += (p.wind * p.depth) + Math.sin(p.angle) * 0.5; // Horizontal drift + side-to-side bobbing
                break;
            case 'traverse':
                p.x += p.speed * p.depth; // Horizontal movement, faster when closer
                p.y += (p.wind * p.depth) + Math.sin(p.angle) * 0.2; // Gentle vertical bob
                break;
            case 'fall':
            default:
                p.y += p.speed * p.depth; // Downward movement, faster when closer
                p.x += p.wind * p.depth; // Horizontal drift, faster when closer
                break;
            }

            const isOffScreen = p.y > overlayHeight + 20 || p.y < -120 || p.x > overlayWidth + 120 || p.x < -120;

            if (isOffScreen) {
                // For continuous effects, reset the particle. For single-pass effects, let it be removed.
                if (this.activeWeather && p.movement !== 'traverse') {
                    if (p.movement === 'float') {
                        p.y = overlayHeight + 20; // Reset floating items to the bottom
                    } else {
                        p.y = -20; // Reset falling items to the top
                    }
                    p.x = Math.random() * overlayWidth;
                } else {
                    // Remove particle if weather has stopped OR if it's a traversing particle that went off-screen
                    p.element.remove();
                    this.particles.splice(i, 1);
                    continue;
                }
            }
            p.element.style.transform = `translate3d(${p.x}px, ${p.y}px, 100px) scale(${p.depth}) rotateX(0deg)`;
        }
    }

    /**
     * Applies the visual effect for the day/night cycle based on the current time of day.
     * @private
     */
    _applyDayNightVisuals() {
        if (!this.dayNightOverlay) return;

        if (!this.isDayNightCycleEnabled) {
            this.dayNightOverlay.style.backgroundColor = 'transparent';
            return;
        }

        // Calculate darkness based on time of day (0 = midnight, 0.5 = noon)
        // The further from noon, the darker it gets.
        const distanceFromNoon = Math.abs(this.timeOfDay - 0.5); // 0 to 0.5
        const maxOpacity = 0.7; // Max darkness at midnight
        const opacity = distanceFromNoon * 2 * maxOpacity; // Scale to 0-maxOpacity range

        this.dayNightOverlay.style.backgroundColor = `rgba(0, 0, 50, ${opacity})`;
    }
}